import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted } from 'vue';

// Components
import BasicButton from '@/ui-components/BasicButton.vue';
import { Platform, PopularTitlesSorting, TitleFilter } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';
import { TitleObjectType } from '@/interfaces/titles';

// Helpers
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TitleEventPayloadGraphql } from '@/helpers/title-helper';

import {
	useConstantStore,
	useFilterStore,
	useLanguageStore,
	useMetaStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';

import {
	GetPopularTitlesDocument,
	GetPopularTitlesQuery,
	GetPopularTitlesQueryVariables,
} from '@/pages/graphql/queries/GetPopularTitles.query';


export default /*#__PURE__*/_defineComponent({
  __name: 'FallbackTitles',
  props: {
    collectionType: null
  },
  emits: ["mounted", "onPosterClick"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'mounted'): void;
	(e: 'onPosterClick', payload: TitleEventPayloadGraphql): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	collectionType: CollectionType;
};

const TitleListGrid = () => import(/* webpackChunkName: "title-list-grid" */ '@/components/list/TitleListGrid.vue');

// Enums & Interfaces




const { t } = useI18n();

const { providersByShortName } = useConstantStore();
const { currentFilters, setFilterValue } = useFilterStore();
const { language, country } = useLanguageStore();
const { isHideSeenDislikedTitlesEnabled, settingsProvider } = useUserStore();

onMounted(() => {
	emits('mounted');
});

const graphqlFilters = computed<TitleFilter>(() => {
	return toGraphqlFilters(currentFilters.value, settingsProvider.value, isHideSeenDislikedTitlesEnabled.value);
});

const queryVariables = computed<GetPopularTitlesQueryVariables>(() => ({
	popularTitlesFilter: graphqlFilters.value,
	watchNowFilter: {
		packages: settingsProvider.value,
	},
	popularTitlesSortBy:
		props.collectionType === CollectionType.NEW ? PopularTitlesSorting.ReleaseYear : PopularTitlesSorting.Popular,
	language: language.value,
	country: country.value,
	platform: Platform.Web,
	first: 16,
}));

const {
	result: popularFallbackData,
	loading,
	onError,
	onResult,
} = useQuery<GetPopularTitlesQuery, GetPopularTitlesQueryVariables>(GetPopularTitlesDocument, queryVariables, {
	fetchPolicy: 'cache-and-network',
});

onError(error =>
	captureMessageForSentry(
		'[GraphQL Get Fallback Titles]:',
		{ error, where: 'Component: FallbackTitles', variables: JSON.stringify(queryVariables.value) },
		'error'
	)
);

const metaStore = useMetaStore();
onResult(({ data }) => {
	// set visible poster count for noindex rules
	if (data) {
		metaStore.setVisiblePosters((data.popularTitles?.edges || []).length);
	}
});

const providerClearNames = computed<string[]>(() =>
	currentFilters.value.providers.map((shortName: string) => providersByShortName.value?.[shortName].clearName)
);

const popularFallbackTitles = computed(() => popularFallbackData.value?.popularTitles.edges || []);

const titlesAmount = computed<number>(() => popularFallbackData.value?.popularTitles.totalCount || 0);

const mediaType = computed<TitleObjectType>(() => currentFilters.value.content_types?.[0] ?? 'releases');

const h3InnerText = computed(() => {
	if (mediaType.value === 'show') {
		if (providerClearNames.value?.length > 1) {
			return props.collectionType === CollectionType.NEW
				? t('WEBAPP_NO_PROVIDERS_OFFER_NEW_SHOW')
				: t('WEBAPP_NO_PROVIDERS_OFFER_SHOW');
		}

		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_NO_PROVIDER_OFFER_NEW_SHOW', { provider: providerClearNames.value?.[0] })
			: t('WEBAPP_NO_PROVIDER_OFFER_SHOW', { provider: providerClearNames.value?.[0] });
	} else if (mediaType.value === 'movie') {
		if (providerClearNames.value?.length > 1) {
			return props.collectionType === CollectionType.NEW
				? t('WEBAPP_NO_PROVIDERS_OFFER_NEW_MOVIE')
				: t('WEBAPP_NO_PROVIDERS_OFFER_MOVIE');
		}

		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_NO_PROVIDER_OFFER_NEW_MOVIE', { provider: providerClearNames.value?.[0] })
			: t('WEBAPP_NO_PROVIDER_OFFER_MOVIE', { provider: providerClearNames.value?.[0] });
	} else {
		if (providerClearNames.value?.length > 1) {
			return props.collectionType === CollectionType.NEW
				? t('WEBAPP_NO_PROVIDERS_OFFER_NEW_ALL')
				: t('WEBAPP_NO_PROVIDERS_OFFER_ALL');
		}

		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_NO_PROVIDER_OFFER_NEW_ALL', { provider: providerClearNames.value?.[0] })
			: t('WEBAPP_NO_PROVIDER_OFFER_ALL', { provider: providerClearNames.value?.[0] });
	}
});

const pInnerText = computed(() => {
	if (mediaType.value === 'show') {
		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_DISCOVER_OTHER_NEW_SHOW')
			: t('WEBAPP_DISCOVER_OTHER_POPULAR_SHOW');
	} else if (mediaType.value === 'movie') {
		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_DISCOVER_OTHER_NEW_MOVIE')
			: t('WEBAPP_DISCOVER_OTHER_POPULAR_MOVIE');
	} else {
		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_DISCOVER_OTHER_NEW_ALL')
			: t('WEBAPP_DISCOVER_OTHER_POPULAR_ALL');
	}
});

const basicButtonInnerText = computed(() => {
	if (mediaType.value === 'show') {
		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_SEE_OTHER_NEW_SHOWS')
			: t('WEBAPP_SEE_OTHER_POPULAR_SHOWS');
	} else if (mediaType.value === 'movie') {
		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_SEE_OTHER_NEW_MOVIES')
			: t('WEBAPP_SEE_OTHER_POPULAR_MOVIES');
	} else {
		return props.collectionType === CollectionType.NEW
			? t('WEBAPP_SEE_OTHER_NEW_ALL')
			: t('WEBAPP_SEE_OTHER_POPULAR_ALL');
	}
});

function onPosterClick(payload: TitleEventPayloadGraphql) {
	emits('onPosterClick', payload);
}

function clearProviders() {
	setFilterValue({ filterName: 'providers', filterValue: [] });
}

return { __sfc: true,TitleListGrid, props, emits, t, providersByShortName, currentFilters, setFilterValue, language, country, isHideSeenDislikedTitlesEnabled, settingsProvider, graphqlFilters, queryVariables, popularFallbackData, loading, onError, onResult, metaStore, providerClearNames, popularFallbackTitles, titlesAmount, mediaType, h3InnerText, pInnerText, basicButtonInnerText, onPosterClick, clearProviders, BasicButton }
}

})